import { api } from './api.ts';
import { cipher } from './session.ts';
import type { DaoApiPagination, DaoApiResponse, DaoPoint, DaoUser } from '@/types/dao.ts';

/**
 * 로그아웃 — 서버에 남은 토큰을 폐기한다.
 *
 * 화면에서 지우는 것만으로는 서버의 토큰이 살아 있다. 공용 PC 를 쓰거나 기기를
 * 잃어버린 경우를 생각하면 서버에서도 끊어야 한다.
 *
 * 본문이 필요 없다. 어느 토큰인지는 `Authorization` 헤더가 말해 준다.
 */
export async function logoutSession(): Promise<void> {
  await api.post('/api/user/logout.json', {});
}

/**
 * 추가정보 등록 요청 본문.
 *
 * 백엔드 `ControllerApiUser::info()` 와 1:1로 맞춰져 있다.
 * 단계별 포인트는 `Core/Vars/Point.php` 에 정의된다.
 *
 *  1단계 birthday + gender      -> USER.UPDATE      0P
 *  2단계 mobile                 -> USER.MOBILE      0P
 *  3단계 agreeMarketing/agreeAd -> USER.MARKETING 500P
 *
 * ★ 2026-09-09 정책 변경으로 **마케팅 동의만 적립한다.** 0P 단계는 서버가 포인트
 *   원장에 아무 기록도 남기지 않는다.
 *
 * **단계를 끝낼 때마다 그 단계의 값만 보낸다.** 마지막에 몰아서 보내면 3단계에서
 * '나중에 하기' 로 빠져나갈 때 앞 단계 보상까지 함께 사라진다.
 * 서버는 담겨 온 항목만 검증·저장·적립한다.
 */
export interface UserInfoRequest {
  userSeq?: number;
  /** 1단계 — YYYYMMDD. gender 와 한 쌍으로 보낸다. */
  birthday?: string;
  /** 1단계 — 'M' | 'F' */
  gender?: string;
  /** 2단계 — 숫자만, 하이픈 없음. SMS 인증을 마친 번호여야 한다. */
  mobile?: string;
  /** 3단계 — 마케팅 활용을 위한 개인정보 수집·이용 동의 */
  agreeMarketing?: boolean;
  /** 3단계 — 광고성 정보 수신 동의 */
  agreeAd?: boolean;
  /** 회원정보 수정 화면 — 한글·영문·숫자 2~10자. 적립과는 무관하다. */
  nickname?: string;
}

/** 이번 요청에서 실제로 적립된 항목 */
export interface UserInfoReward {
  code: string;
  name: string;
  point: number;
}

export interface UserInfoResponse extends DaoApiResponse<DaoUser> {
  /**
   * 실제 적립 결과. 이미 받았거나 0P 인 단계는 빠지므로 total 이
   * `INFO_TOTAL_POINT` 보다 작을 수 있다.
   */
  rewards?: {
    total: number;
    items: UserInfoReward[];
  };
}

/**
 * 추가정보 등록. 성공 시 `data` 로 갱신된 회원 정보가 온다.
 *
 * 본문 전체를 AES-GCM 으로 감싼 `{ iv, cipher }` 형태로 보낸다.
 * 백엔드는 `ControllerApi::getCryptPayload()` 에서 같은 규약으로 푼다.
 * 키는 프론트 `VITE_ENCRYPT` = 백엔드 `API_CRYPTION_KEY` 로 같아야 한다.
 */
export async function submitUserInfo(payload: UserInfoRequest): Promise<UserInfoResponse> {
  const encrypted = await cipher.encryptJson(payload);
  return api.post<UserInfoResponse>('/api/user/info.json', encrypted);
}

/** 마이페이지 응답 — 회원 정보 + 포인트 내역 첫 장 */
export interface MypageData {
  user: DaoUser;
  point: DaoApiPagination<DaoPoint>;
}

/** 마이페이지 (회원 정보 + 포인트 내역 미리보기) */
export async function fetchMypage(userSeq?: number): Promise<DaoApiResponse<MypageData>> {
  const encrypted = await cipher.encryptJson({ userSeq });
  return api.post<DaoApiResponse<MypageData>>('/api/user/mypage.json', encrypted);
}

/**
 * 포인트 내역 (한 장 10건).
 *
 * `page` 는 1부터. 백엔드가 예전에는 이 값을 무시하고 늘 1페이지만 돌려줘서
 * 무한스크롤이 같은 항목만 반복해 받았다. 지금은 반영된다.
 */
export async function fetchPointHistory(
  userSeq: number | undefined,
  page: number
): Promise<DaoApiResponse<DaoApiPagination<DaoPoint>>> {
  const encrypted = await cipher.encryptJson({ userSeq, page });
  return api.post<DaoApiResponse<DaoApiPagination<DaoPoint>>>('/api/user/history.json', encrypted);
}

////////////////////////////////////////////////////////////////////////////////
/// 휴대폰 SMS 인증
///
/// 발송과 확인이 같은 엔드포인트(`/api/user/mobile.json`)를 쓰고 `action` 으로
/// 갈린다. 백엔드 라우터가 파일명을 `[-_,.]` 로 쪼개 메서드명을 만들기 때문에
/// `mobile.send.json` 같은 경로는 메서드명이 붙어버린다.
///
/// 인증 상태는 서버 세션에 남으므로 `credentials: 'include'` 가 필요하다
/// (공용 api 래퍼가 이미 그렇게 보낸다).
////////////////////////////////////////////////////////////////////////////////

export interface MobileSendResult {
  /** 인증번호 유효시간(초) */
  expiresIn: number;
  /** 재발송까지 대기 시간(초) */
  resendIn: number;
}

/** 인증번호 발송 — 실제 문자가 나간다 */
export async function sendMobileCode(
  userSeq: number | undefined,
  mobile: string
): Promise<DaoApiResponse<MobileSendResult>> {
  const encrypted = await cipher.encryptJson({ userSeq, mobile, action: 'send' });
  return api.post<DaoApiResponse<MobileSendResult>>('/api/user/mobile.json', encrypted);
}

/** 인증번호 확인 */
export async function verifyMobileCode(
  userSeq: number | undefined,
  mobile: string,
  code: string
): Promise<DaoApiResponse<{ verified: boolean }>> {
  const encrypted = await cipher.encryptJson({ userSeq, mobile, code, action: 'verify' });
  return api.post<DaoApiResponse<{ verified: boolean }>>('/api/user/mobile.json', encrypted);
}
